import { useState, useEffect, useRef, RefObject } from 'react';
import { classNameList } from 'utils/property';
import { HEADER_MOBILE_HEIGHT } from '../constants';
import styles from '../Header.module.scss';

export enum NavClassNameListType {
  AbsoluteNav,
  ReadyToShowPeekNav,
  ShowPeekNav,
  HidePeekNav
}

const convertNavClassNameListTypeToclassNameList = (type: NavClassNameListType): string => {
  switch (type) {
    case NavClassNameListType.ReadyToShowPeekNav:
      return classNameList(styles.navFixed, styles.navFixedHidden);
    case NavClassNameListType.ShowPeekNav:
      return classNameList(styles.navFixed, styles.navFixedVisible);
    case NavClassNameListType.HidePeekNav:
      return classNameList(styles.navFixed, styles.navFixedHidden, styles.navFixedHiddenWithAnimation);
    case NavClassNameListType.AbsoluteNav:
    default:
      return '';
  }
};

const peekNavIsShowing = (type: NavClassNameListType): boolean => {
  return convertNavClassNameListTypeToclassNameList(type).includes(styles.navFixedVisible);
};

const peekNavIsReadyToShow = (type: NavClassNameListType): boolean => {
  return convertNavClassNameListTypeToclassNameList(type).includes(styles.navFixed);
};

export const getNavClassNameListType = (
  currentScrollY: number,
  prevScrollY: number,
  prevNavClassNameListType: NavClassNameListType,
  menuVisible: boolean,
  navigationOffsetY = 0
): NavClassNameListType => {
  const scrollDifference = currentScrollY - prevScrollY;
  const scrollingDown = scrollDifference > 1;
  const scrollingUpFast = scrollDifference <= -5;
  const scrolledToTop = currentScrollY <= navigationOffsetY;
  const headerPassed = currentScrollY >= navigationOffsetY + HEADER_MOBILE_HEIGHT;
  const headerPassed5X = currentScrollY >= navigationOffsetY + HEADER_MOBILE_HEIGHT * 5;
  const prevPeekNavIsVisible = peekNavIsShowing(prevNavClassNameListType);
  const prevPeekNavIsReadyToShow = peekNavIsReadyToShow(prevNavClassNameListType);

  // Always show the absolute nav on top:
  if (scrolledToTop) {
    return NavClassNameListType.AbsoluteNav;
  }

  // Hide the peek nav:
  if (scrollingDown && headerPassed && prevPeekNavIsVisible && !menuVisible) {
    return NavClassNameListType.HidePeekNav;
  }

  // Show the peek nav:
  if (scrollingUpFast && headerPassed5X) {
    return NavClassNameListType.ShowPeekNav;
  }

  // Put the animation ready for possible future peek nav showing:
  if (headerPassed && !prevPeekNavIsReadyToShow) {
    return NavClassNameListType.ReadyToShowPeekNav;
  }

  // Use the absolute nav near the top:
  if (!headerPassed && !prevPeekNavIsVisible) {
    return NavClassNameListType.AbsoluteNav;
  }

  return prevNavClassNameListType;
};

export const useNavigationClassNameList = (navigationOffsetY: number, navigationRef: RefObject<HTMLElement>) => {
  const prevScrollY = useRef(0);
  const [prevNavClassNameListType, setPrevNavClassNameListType] = useState(NavClassNameListType.AbsoluteNav);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const navclassNameListType = getNavClassNameListType(
        currentScrollY,
        prevScrollY.current,
        prevNavClassNameListType,
        navigationRef?.current?.getAttribute('aria-expanded') === 'true',
        navigationOffsetY
      );

      prevScrollY.current = currentScrollY;
      setPrevNavClassNameListType(navclassNameListType);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [prevNavClassNameListType]);

  return convertNavClassNameListTypeToclassNameList(prevNavClassNameListType);
};
